import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  cancelSopGeneration,
  createSop,
  createSopSection,
  deleteSop,
  fetchSop,
  fetchSops,
  fetchVideos,
  regenerateSop,
  reorderSopSections,
  saveSopMarkup,
  updateSop,
  updateSopSection,
  type SopDetail,
  type SopMarkupAnnotation,
} from '../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { sopKeys } from '../../queries/sop-synthesizer/keys.ts';
import { getApiErrorMessage } from '../../request/getApiErrorMessage.ts';

/**
 * Reuse previous signed frame URLs when the same artifact ids come back.
 * Each SOP GET re-signs GCS URLs (new query string) which would otherwise reload
 * every <img> and cause a visible blink on markup/edit saves.
 */
function keepStableFrameUrls(prev: SopDetail | undefined, next: SopDetail): SopDetail {
  if (!prev?.frames || !next.frames) return next;
  const frames: Record<string, string> = { ...next.frames };
  for (const id of Object.keys(frames)) {
    if (prev.frames[id]) frames[id] = prev.frames[id];
  }
  const citations = Array.isArray(next.citations)
    ? next.citations.map((c) => {
        const fid = c.frame_artifact_id;
        if (fid && frames[fid]) return { ...c, frame_url: frames[fid] };
        return c;
      })
    : next.citations;
  return { ...next, frames, citations };
}

export const useSopVideos = (enabled = true) =>
  useQuery({
    queryKey: sopKeys.videos(),
    queryFn: fetchVideos,
    enabled,
    // Keep the picker current while an administrator is preparing a video.
    refetchInterval: (q) => (q.state.data?.some((v) => v.media_status === 'processing') ? 5000 : false),
  });

export const useSops = () =>
  useQuery({
    queryKey: sopKeys.list(),
    queryFn: fetchSops,
    // Poll while any SOP is still being generated.
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'generating') ? 4000 : false),
  });

export const useSop = (id: string | undefined) => {
  const qc = useQueryClient();
  return useQuery({
    queryKey: sopKeys.detail(id ?? ''),
    queryFn: async () => {
      const next = await fetchSop(id!);
      const prev = qc.getQueryData<SopDetail>(sopKeys.detail(id!));
      return keepStableFrameUrls(prev, next);
    },
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.sop.status === 'generating' ? 3000 : false),
  });
};

export const useCreateSop = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSop,
    onSuccess: () => qc.invalidateQueries({ queryKey: sopKeys.list() }),
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start the SOP')),
  });
};

export const useRegenerateSop = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (instructions?: string) => regenerateSop(id, instructions),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not start generation')),
  });
};

export const useDeleteSop = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSop,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('SOP deleted');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not delete the SOP')),
  });
};

export const useUpdateSop = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string; description?: string; status?: 'draft' | 'in_review' | 'changes_requested' | 'approved' | 'published' }) =>
      updateSop(id, body),
    onSuccess: (_sop, body) => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success(body.status && body.title === undefined && body.description === undefined ? 'Status updated' : 'Details saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the details')),
  });
};

export const useUpdateSopSection = (sopId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, body, heading }: { sectionId: string; body: string; heading?: string }) =>
      updateSopSection(sopId, sectionId, heading !== undefined ? { body, heading } : { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(sopId) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('Section saved');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not save the section')),
  });
};

export const useCreateSopSection = (sopId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { heading: string; body?: string }) => createSopSection(sopId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(sopId) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('Section added');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not add the section')),
  });
};

export const useReorderSopSections = (sopId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sectionIds: string[]) => reorderSopSections(sopId, sectionIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(sopId) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'failed to reorder sections')),
  });
};

export const useCancelSopGeneration = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelSopGeneration(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: sopKeys.detail(id) });
      qc.invalidateQueries({ queryKey: sopKeys.list() });
      toast.success('Generation cancelled');
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not cancel')),
  });
};

export const useSaveSopMarkup = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (annotations: SopMarkupAnnotation[]) => saveSopMarkup(id, annotations),
    onSuccess: (saved) => {
      // Patch markup only — avoid full SOP GET (new GCS signed URLs → image blink).
      qc.setQueryData<SopDetail>(sopKeys.detail(id), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          sop: { ...prev.sop, markup_annotations: saved },
        };
      });
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not update highlight or comment')),
  });
};
